import find from 'lodash/find';
import get from 'lodash/get';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { NgbAccordionDirective } from '@ng-bootstrap/ng-bootstrap';
import {
  CustomComponent,
  IOutlet,
  IOutletAccordionOptions,
  IScheduleOverride,
  ngHelper,
  OUTLET_SCHEDULE_TYPES,
  UiContextService
} from '@cakeiteasy/web-utils';
import { ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { isPlatformBrowser } from '@angular/common';
import { IOutletMapFont } from '../../models/i-outlet-map-font';
import { OverlayScrollbarsDirective } from 'overlayscrollbars-ngx';
import { ClickScrollPlugin, OverlayScrollbars } from 'overlayscrollbars';
import type { EventListeners, PartialOptions } from 'overlayscrollbars';

OverlayScrollbars.plugin(ClickScrollPlugin);

@Component({
  standalone: false,
  selector: 'ui-map-with-outlets-accordion',
  templateUrl: './ui-map-with-outlets-accordion.component.html',
  styleUrls: ['./ui-map-with-outlets-accordion.component.scss']
})
export class UiMapWithOutletsAccordionComponent extends CustomComponent implements OnChanges, AfterViewInit {
  @Input() outlets: IOutlet[];
  @Input() scheduleOverridesByOutletId: { [outletId: number]: IScheduleOverride[] } = {};
  @Input() disabledOutletIds: number[];
  @Input() pickUp: { outletId?: number, type?: string, outletName?: string };
  @Input() onBackground: boolean;
  @Input() showNext: boolean;
  @Input() nextText: string;
  @Input() shiftMapToRight: boolean;
  @Input() isOutletSelectionStateEnabled = true;

  // max zoom for initial rendering could not be bigger than the map max zoom
  @Input() initialMaxZoom = 16;

  @Input() disabledSimpleScroll = false;
  @Input() outletAccordionOptions: IOutletAccordionOptions;
  @Input() scheduleType: string = OUTLET_SCHEDULE_TYPES.WORKING_DAYS;

  @Input() accordionFontData: IOutletMapFont;
  public accordionFont: string;

  @Output() onNext = new EventEmitter<IOutlet>();
  @Output() onOutletChanged = new EventEmitter<IOutlet>();

  @ViewChild('acc', { static: false }) accordion: NgbAccordionDirective;
  @ViewChild('outletListContainer', { static: false }) outletListContainer: ElementRef<HTMLDivElement>;
  @ViewChild('outletAccordionWrap', { static: false }) outletAccordionWrap: ElementRef<HTMLDivElement>;
  @ViewChild('simpleAccordionWrap', { static: false }) simpleAccordionWrap: ElementRef<HTMLDivElement>;
  @ViewChild('mapContainer', { static: false }) mapContainer: ElementRef<HTMLDivElement>;
  @ViewChild('outletScrollbar', { static: false }) outletScrollbar?: ElementRef<HTMLDivElement>;
  @ViewChild('outletScrollbar', { read: OverlayScrollbarsDirective, static: false }) overlayScrollbar?: OverlayScrollbarsDirective;

  public scrollbarOptions: PartialOptions = {
    overflow: { x: 'hidden', y: 'scroll' },
    scrollbars: {
      theme: 'os-theme-outlet',
      visibility: 'visible',
      autoHide: 'never',
      dragScroll: true,
      clickScroll: true
    }
  };
  public scrollbarEvents: EventListeners = {
    scroll: () => this.onOutletListScroll()
  };

  public destinationPrefix = 'outletDestination_';
  public isOutletsContainerOverflow = false;
  public expandedOutlet: IOutlet;
  public selectedOnMapOutlet = signal<IOutlet>(null);

  public yReachEnd = false;
  public yReachStart = true;

  constructor(public uiContext: UiContextService,
              private _scrollToService: ScrollToService,
              private _ref: ChangeDetectorRef,
              @Inject(PLATFORM_ID) private _platformId: Object) {
    super();
  }

  @HostListener('window:resize')
  handleResize() {
    this.compareClientHeight();
  }


  ngOnChanges(changes: SimpleChanges): void {
    ngHelper.ifChanged(changes, 'pickUp', (pickUp) => {
      if (pickUp) {
        const outlet = find(this.outlets, { id: pickUp.outletId });
        this.expandedOutlet = outlet;
        this.onOutletChanged.emit(outlet);
      }
    });

    ngHelper.ifChanged(changes, 'outlets', (outlets) => {
      if (outlets && outlets.length === 1) {
        this.expandedOutlet = outlets[0];
        this.onOutletChanged.emit(this.expandedOutlet);
      }

      this.compareClientHeight();
    });

    ngHelper.ifChanged(changes, 'accordionFontData', (font: IOutletMapFont) => {
      this.accordionFont = font ? `${font.family}, ${font.category}` : '';
    });
  }

  ngAfterViewInit(): void {
    this.compareClientHeight();

    if (isPlatformBrowser(this._platformId) && this.overlayScrollbar && this.outletScrollbar) {
      this.overlayScrollbar.osInitialize(this.outletScrollbar.nativeElement);
    }
  }

  public onOutletListScroll(): void {
    const element = this.overlayScrollbar?.osInstance()?.elements().scrollOffsetElement;

    if (!element) {
      return;
    }

    this.yReachStart = element.scrollTop === 0;
    this.yReachEnd = Math.ceil(element.scrollTop + element.clientHeight) >= element.scrollHeight;
    this._ref.detectChanges();
  }

  public perfectScrollToOutlet(target: string) {
    setTimeout(() => {
      const scrollElement = this.overlayScrollbar?.osInstance()?.elements().scrollOffsetElement;
      const element = this.outletAccordionWrap?.nativeElement.querySelector(`#${target}`) as HTMLElement;
      const top = element.getBoundingClientRect().top
        - scrollElement.getBoundingClientRect().top
        + scrollElement.scrollTop
        - 20;

      scrollElement.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
  }

  public simpleScrollToOutlet(target: string) {
    if (!this.disabledSimpleScroll) {
      setTimeout(() => {
        this._scrollToService.scrollTo({
          duration: 100,
          offset: -10,
          target: this.simpleAccordionWrap.nativeElement.querySelector(`#${target}`) as HTMLElement
        });
      }, 400);
    }
  }

  setExpandedOutlet(outlet) {
    if (!this.expandedOutlet || this.expandedOutlet && this.expandedOutlet.id !== outlet.id) {
      this.expandedOutlet = { ...outlet };
      this.onOutletChanged.emit(this.expandedOutlet);
    }
  }

  setMapOutlet(outlet: any) {
    this.expandedOutlet = outlet;
    this.selectedOnMapOutlet.set(outlet);
    this.onOutletChanged.emit(outlet);

    if (this.outlets.length > 1) {
      this.scrollToMapBottom();
    }
  }

  public scrollToMapBottom() {
    if (isPlatformBrowser(this._platformId) && this.mapContainer) {
      const windowHeight = window.innerHeight;
      const { bottom } = this.mapContainer.nativeElement.getBoundingClientRect();

      if (windowHeight > bottom + 20) {
        return;
      }

      const offset = bottom - windowHeight + 40;

      this._scrollToService.scrollTo({
        offset,
        duration: 200,
      });
    }
  }

  private compareClientHeight(): void {
    setTimeout(() => {
      const outletListHeight = get(this.outletListContainer, 'nativeElement.clientHeight');
      const accordionHeight = get(this.outletAccordionWrap, 'nativeElement.clientHeight');
      if (outletListHeight && accordionHeight) {
        this.isOutletsContainerOverflow = accordionHeight > outletListHeight;
      }
    });
  }
}
